import {
	CampaignOutlined,
	CheckCircleOutline,
	Close,
	DirectionsCarOutlined,
	EventOutlined,
	LocalOfferOutlined,
	Payments,
	PersonOutline,
	Storefront,
} from '@mui/icons-material';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { ReactNode } from 'react';
import { IndicacaoSolicitacaoGanhos, IndicacaoSolicitacaoLista } from '../../../services/indicacao';
import { formatCurrencyBRL } from '../../../utils/mask/MaskUtils';
import { formatDate, withAlpha } from '../indicacao.utils';
import { IndicacaoButton } from './IndicacaoButton';

const CampoResumo = ({
	icon,
	label,
	value,
	destaque,
}: {
	icon: ReactNode;
	label: string;
	value: string;
	destaque?: boolean;
}) => (
	<Box
		sx={{
			p: 1.5,
			borderRadius: 2.5,
			minWidth: 0,
			border: '1px solid',
			borderColor: destaque ? 'rgba(5, 150, 105, 0.28)' : '#e2e8f0',
			bgcolor: destaque ? 'rgba(5, 150, 105, 0.06)' : '#f8fafc',
		}}
	>
		<Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
			<Box sx={{ display: 'flex', color: destaque ? 'rgb(5, 150, 105)' : 'rgb(100, 116, 139)' }}>
				{icon}
			</Box>
			<Typography
				fontSize="0.7rem"
				fontWeight={700}
				letterSpacing="0.06em"
				textTransform="uppercase"
				color="text.secondary"
			>
				{label}
			</Typography>
		</Box>
		<Typography fontWeight={700} fontSize="0.95rem" noWrap title={value}>
			{value}
		</Typography>
	</Box>
);

interface AceiteDialogProps {
	aberto: IndicacaoSolicitacaoLista | null;
	ganhos: IndicacaoSolicitacaoGanhos | null;
	carregando: boolean;
	decidindo: boolean;
	onFechar: () => void;
	onConfirmar: () => void;
}

export function AceiteDialog({
	aberto,
	ganhos,
	carregando,
	decidindo,
	onFechar,
	onConfirmar,
}: AceiteDialogProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const primary = theme.palette.primary.main;

	return (
		<Dialog
			open={aberto !== null}
			onClose={onFechar}
			fullWidth
			maxWidth="md"
			aria-labelledby="aceite-dialog-titulo"
			PaperProps={{
				elevation: 0,
				sx: {
					borderRadius: 4,
					overflow: 'hidden',
					border: '1px solid #e2e8f0',
				},
			}}
		>
			<Box
				sx={{
					px: 3,
					pt: 2.5,
					pb: 2,
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: 2,
					background: `linear-gradient(135deg, ${withAlpha(primary, 0.08)} 0%, #ffffff 58%)`,
					borderBottom: '1px solid #f1f5f9',
				}}
			>
				<Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
					<Box
						sx={{
							width: 44,
							height: 44,
							borderRadius: 2.5,
							flexShrink: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: primary,
							bgcolor: withAlpha(primary, 0.12),
						}}
					>
						<CampaignOutlined />
					</Box>
					<Box minWidth={0}>
						<Typography id="aceite-dialog-titulo" fontWeight={800} fontSize="1.15rem">
							Anúncios do pedido
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							Revise os ganhos antes de confirmar o aceite.
						</Typography>
					</Box>
				</Box>
				<IconButton
					size="small"
					onClick={onFechar}
					disabled={decidindo}
					aria-label="Fechar"
					sx={{ color: 'text.secondary' }}
				>
					<Close fontSize="small" />
				</IconButton>
			</Box>
			<DialogContent sx={{ px: 3, py: 2.5 }}>
				{carregando ? (
					<Box display="flex" flexDirection="column" alignItems="center" gap={1.5} py={5}>
						<CircularProgress size={28} sx={{ color: primary }} />
						<Typography fontSize="0.875rem" color="text.secondary">
							Carregando anúncios...
						</Typography>
					</Box>
				) : (
					<Box display="flex" flexDirection="column" gap={2.5}>
						<Box
							display="grid"
							gridTemplateColumns={isMobile ? '1fr' : '1.4fr 1fr 1fr'}
							gap={1.25}
						>
							<CampoResumo
								icon={<PersonOutline sx={{ fontSize: 16 }} />}
								label="Solicitante"
								value={ganhos?.nome_solicitante || aberto?.nome_solicitante || 'Solicitante'}
							/>
							<CampoResumo
								icon={<EventOutlined sx={{ fontSize: 16 }} />}
								label="Pedido em"
								value={aberto?.data_solicitacao ? formatDate(aberto.data_solicitacao) : '—'}
							/>
							<CampoResumo
								icon={<Payments sx={{ fontSize: 16 }} />}
								label="Valor total"
								destaque
								value={formatCurrencyBRL(ganhos?.valor_solicitado ?? aberto?.valor_solicitado ?? 0)}
							/>
						</Box>

						{aberto?.observacao_solicitante && (
							<Alert severity="info" sx={{ borderRadius: 2 }}>
								{aberto.observacao_solicitante}
							</Alert>
						)}

						<Box>
							<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
								<Typography
									fontSize="0.72rem"
									fontWeight={700}
									letterSpacing="0.06em"
									textTransform="uppercase"
									color="text.secondary"
								>
									Anúncios
								</Typography>
								<Chip
									size="small"
									label={`${(ganhos?.ganhos ?? []).length} ${
										(ganhos?.ganhos ?? []).length === 1 ? 'anúncio' : 'anúncios'
									}`}
									sx={{ fontWeight: 600, bgcolor: '#f1f5f9' }}
								/>
							</Box>
							{(ganhos?.ganhos ?? []).length === 0 ? (
								<Alert severity="info" sx={{ borderRadius: 2 }}>
									Nenhum anúncio encontrado neste pedido.
								</Alert>
							) : (
								<Box
									display="grid"
									gridTemplateColumns={isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))'}
									gap={1.5}
								>
									{ganhos?.ganhos.map((ganho, index) => (
										<Box
											key={`${ganho.num_id_anuncio}-${index}`}
											sx={{
												p: 1.75,
												borderRadius: 2.5,
												border: '1px solid #e2e8f0',
												bgcolor: '#fff',
												display: 'flex',
												flexDirection: 'column',
												gap: 1.25,
												transition: 'border-color 0.2s, box-shadow 0.2s',
												'&:hover': {
													borderColor: withAlpha(primary, 0.4),
													boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
												},
											}}
										>
											<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
												<Box display="flex" alignItems="center" gap={1} minWidth={0}>
													<Box
														sx={{
															width: 34,
															height: 34,
															borderRadius: 1.5,
															flexShrink: 0,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															color: primary,
															bgcolor: withAlpha(primary, 0.1),
														}}
													>
														<LocalOfferOutlined sx={{ fontSize: 18 }} />
													</Box>
													<Box minWidth={0}>
														<Typography
															fontSize="0.68rem"
															fontWeight={700}
															letterSpacing="0.05em"
															textTransform="uppercase"
															color="text.secondary"
														>
															Anúncio
														</Typography>
														<Typography fontWeight={800}>#{ganho.num_id_anuncio}</Typography>
													</Box>
												</Box>
												<Typography fontWeight={800} color="rgb(5, 150, 105)" whiteSpace="nowrap">
													{formatCurrencyBRL(ganho.valor)}
												</Typography>
											</Box>
											<Divider />
											<Box display="flex" alignItems="center" gap={0.75}>
												<Storefront sx={{ fontSize: 16, color: 'text.secondary' }} />
												<Typography fontSize="0.75rem" color="text.secondary">
													Origem
												</Typography>
												<Typography fontSize="0.875rem" fontWeight={600} ml="auto" noWrap>
													{ganho.origem || 'Indicação'}
												</Typography>
											</Box>
											<Box>
												<Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
													<DirectionsCarOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
													<Typography fontSize="0.75rem" color="text.secondary">
														Veículos
													</Typography>
												</Box>
												{ganho.veiculos.length > 0 ? (
													<Box display="flex" flexWrap="wrap" gap={0.75}>
														{ganho.veiculos.map((veiculo) => (
															<Chip
																key={veiculo}
																size="small"
																label={veiculo}
																sx={{ bgcolor: '#f1f5f9', fontWeight: 500, maxWidth: '100%' }}
															/>
														))}
													</Box>
												) : (
													<Typography fontSize="0.8rem" color="text.secondary">
														Veículo não informado
													</Typography>
												)}
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Box>
				)}
			</DialogContent>
			<DialogActions
				sx={{
					px: 3,
					py: 2,
					gap: 1,
					borderTop: '1px solid #f1f5f9',
					bgcolor: '#fafafa',
				}}
			>
				<IndicacaoButton
					variant="outlined"
					label="Fechar"
					icon={<Close fontSize="small" />}
					onClick={onFechar}
					disabled={decidindo}
				/>
				<IndicacaoButton
					variant="success"
					label={decidindo ? 'Confirmando...' : 'Confirmar aceite'}
					icon={<CheckCircleOutline fontSize="small" />}
					disabled={carregando || decidindo || aberto === null}
					loading={decidindo}
					onClick={onConfirmar}
				/>
			</DialogActions>
		</Dialog>
	);
}
